
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';

interface MFAVerificationProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({ onSuccess, onCancel }) => {
  const [code, setCode] = useState<string>("");
  const [isVerifying, setIsVerifying] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.length < 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a valid 6-digit code",
        variant: "destructive",
      });
      return;
    }

    setIsVerifying(true);
    try {
      // First get the MFA challenge
      const { data: factor } = await supabase.auth.mfa.listFactors();
      
      if (factor.totp.length === 0) {
        toast({
          title: "MFA not setup",
          description: "You haven't set up MFA on your account",
          variant: "destructive",
        });
        setIsVerifying(false);
        return;
      }
      
      const factorId = factor.totp[0].id;
      
      // Create a challenge
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });
      
      if (challengeError) throw challengeError;
      
      // Verify the challenge
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      
      if (verifyError) throw verifyError;
      
      toast({
        title: "Success",
        description: "MFA verification successful",
      });
      
      onSuccess();
    } catch (error) {
      console.error("MFA verification error:", error);
      toast({
        title: "Verification failed",
        description: "The code you entered is invalid or expired. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto bg-tokscript-card border-gray-800">
      <CardHeader>
        <CardTitle className="text-xl text-center">Two-Factor Authentication</CardTitle>
        <CardDescription className="text-center">
          Enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <div className="flex justify-center py-4">
            <InputOTP maxLength={6} value={code} onChange={setCode}>
              <InputOTPGroup>
                <InputOTPSlot index={0} className="bg-gray-800 border-gray-700" />
                <InputOTPSlot index={1} className="bg-gray-800 border-gray-700" />
                <InputOTPSlot index={2} className="bg-gray-800 border-gray-700" />
                <InputOTPSlot index={3} className="bg-gray-800 border-gray-700" />
                <InputOTPSlot index={4} className="bg-gray-800 border-gray-700" />
                <InputOTPSlot index={5} className="bg-gray-800 border-gray-700" />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isVerifying}
            className="border-gray-700 hover:bg-gray-800"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={code.length < 6 || isVerifying}
            className="bg-tokscript-cta hover:bg-tokscript-cta/90 text-black"
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying
              </>
            ) : (
              'Verify'
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default MFAVerification;
